import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useIsWebView, useToken, useWebViewLoading, useDoctorRouteGuard } from '../../../shared/hooks';
import { PdfButton } from '../../../shared/components/PdfButton';
import { DoctorRouteDialog } from '../../../shared/components/DoctorRouteDialog';

import type { Inquiry } from '../types';
import { useInquiryDetail } from '../hooks/useInquiryDetail';
import { useInquiryFiles } from '../hooks/useInquiryFiles';
import type { FileType } from '../data/inquiryFilesRepository';

import './InquiryDetail.css';

const getDisplayName = (fileName: string): string => {
  return fileName.replace(/\.pdf$/i, '');
};

const getFileTypeForStatus = (status: Inquiry['status']): FileType | null => {
  switch (status) {
    case '調査見積もり済':
    case '見積もりサイン済':
      return 'estimate';
    case '作業報告書確認':
    case '作業完了':
      return 'reportAfter';
    default:
      return null;
  }
};

/**
 * 書類確認画面（PDF一覧 + サインへ進むボタン）
 *
 * /inquiries/:id/documents でアクセス。
 * ステータスに応じたタイトルとファイル一覧を表示する。
 */
function InquiryDocuments() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isWebView = useIsWebView();
  const token = useToken();

  const inquiryId = id || '';
  const { data: inquiry, isLoading, error } = useInquiryDetail(inquiryId, token);
  const { shouldBlock, handleConfirm: handleGuardConfirm } = useDoctorRouteGuard(id, token);

  const fileType = inquiry ? getFileTypeForStatus(inquiry.status) : null;

  const { files, isLoading: isFilesLoading } = useInquiryFiles(
    fileType ? inquiryId : '',
    fileType || 'estimate',
    token
  );

  const storageKey = `viewedDocs_${id}`;

  const [viewedDocs, setViewedDocs] = useState<Record<string, boolean>>(() => {
    const savedState = sessionStorage.getItem(storageKey);
    return savedState ? JSON.parse(savedState) : {};
  });

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(viewedDocs));
  }, [viewedDocs, storageKey]);

  const needsFiles = fileType !== null;
  const combinedLoading = isLoading || (needsFiles && isFilesLoading);
  const contentRef = useWebViewLoading(combinedLoading);

  if (combinedLoading) {
    return <div className="detail-container" />;
  }

  // ドクタールートに切り替わっていた場合は Flutter ダッシュボードへ強制復帰
  if (shouldBlock) {
    return <DoctorRouteDialog onConfirm={handleGuardConfirm} />;
  }

  if (error || !inquiry) {
    return <div>お問い合わせが見つかりません。</div>;
  }

  // このページに該当しないステータスの場合は詳細に戻す
  if (inquiry.status !== '調査見積もり済' && inquiry.status !== '作業報告書確認') {
    navigate(`/inquiries/${id}`, { replace: true });
    return null;
  }

  const handleViewDoc = (fileName: string) => {
    setViewedDocs(prev => ({ ...prev, [fileName]: true }));
  };

  const allFilesViewed = files.length > 0 && files.every(file => viewedDocs[file.fileName]);

  // 次へボタンの遷移先
  // - 調査見積もり済（注文サイン前）: 支払方法選択画面（order mode）を経由してからサインへ
  // - 作業報告書確認（作業完了サイン前）: 直接サイン画面へ
  const handleNext = () => {
    if (inquiry.status === '調査見積もり済') {
      navigate(`/inquiries/${id}/payment-method?mode=order`, {
        state: { fromStatus: inquiry.status },
      });
      return;
    }
    navigate(`/inquiries/${id}/sign`, { state: { fromStatus: inquiry.status } });
  };

  // PPTX指示(0506)
  // - 調査見積もり済: タイトル「見積・注文確認」/ 説明「各種書類をご確認ください。\nご注文をされる場合は、すべての書類をご確認の上、注文手続きへお進みください。」/ ボタン「注文手続きへ進む」
  // - 作業報告書確認: タイトル「作業報告・納品確認」/ 説明「書類をご確認ください。\n納品手続きをされる場合は、書類をご確認の上、納品手続きへお進みください。\n納品手続きの完了後、お支払い手続きへ進みます。」/ ボタン「納品手続きへ進む」
  const isOrderFlow = inquiry.status === '調査見積もり済';
  const title = isOrderFlow ? '見積・注文確認' : '作業報告・納品確認';
  const instructionText = isOrderFlow
    ? '各種書類をご確認ください。\nご注文をされる場合は、すべての書類をご確認の上、注文手続きへお進みください。'
    : '書類をご確認ください。\n納品手続きをされる場合は、書類をご確認の上、納品手続きへお進みください。\n納品手続きの完了後、お支払い手続きへ進みます。';
  const nextButtonText = isOrderFlow ? '注文手続きへ進む' : '納品手続きへ進む';

  return (
    <div ref={contentRef} className="detail-container">
      <main className="detail-content">
        <h2 className="content-title">{title}</h2>
        <p className="instruction-text" style={{ whiteSpace: 'pre-line' }}>{instructionText}</p>
        {files.map(file =>
          <PdfButton
            key={file.fileId}
            name={getDisplayName(file.fileName)}
            url={file.url}
            viewed={!!viewedDocs[file.fileName]}
            onView={() => handleViewDoc(file.fileName)}
          />
        )}
      </main>
      <footer className="detail-footer">
        <button className="button-primary" disabled={!allFilesViewed} onClick={handleNext}>
          {nextButtonText}
        </button>
        {!isWebView && (
          <button className="button-secondary" onClick={() => navigate(-1)}>戻る</button>
        )}
      </footer>
    </div>
  );
}

export default InquiryDocuments;
